import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

/* ═══════════════════════════════════════════
   EDITABLE: Navigation links
═══════════════════════════════════════════ */
const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Products', href: '#products' },
  { label: 'Achievements', href: '#achievements' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Contact', href: '#contact' },
];

const PHONE_NUMBER = '9870281491'; // EDITABLE

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header
      data-testid="sticky-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass-header shadow-md' : 'bg-white'
      }`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut', delay: 2.8 }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 flex items-center justify-between h-20 md:h-24">
        {/* Logo */}
        <a href="#home" data-testid="logo-link" className="flex items-center">
          <motion.img
            src="https://customer-assets.emergentagent.com/job_financial-evolution-1/artifacts/o920clpu_Ashwamedh%20Logo%20-%201.png"
            alt="Ashwamedh 360"
            className="h-16 md:h-20 w-auto object-contain"
            initial={{ scale: 1.3 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 3 }}
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-medium text-[#49566B] hover:text-[#D4AF37] transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:+91${PHONE_NUMBER}`}
            data-testid="header-phone-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-[#0A192F] text-[#D4AF37] text-sm font-semibold rounded-full hover:scale-105 hover:shadow-lg transition-all duration-300 border border-[#D4AF37]/30"
          >
            <Phone size={14} />
            +91 {PHONE_NUMBER}
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-[#0A192F]"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
          >
            <nav className="flex flex-col py-4 px-6 gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm font-medium text-[#49566B] hover:text-[#D4AF37] py-2"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={`tel:+91${PHONE_NUMBER}`}
                data-testid="mobile-phone-btn"
                className="mt-2 px-5 py-2.5 bg-[#0A192F] text-[#D4AF37] text-sm font-semibold rounded-full text-center"
              >
                Call: +91 {PHONE_NUMBER}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
